/**
 * CustomerList Page - T068
 */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  GlassPanel,
  Button,
  EmptyState,
  FilterPill,
  LoadingSpinner,
  PageHeader,
  Pagination,
  SearchInput,
} from '../components/ui';
import { CustomerCard } from '../components/customers/CustomerCard';
import { useCustomerList, type CustomerListQuery } from '../hooks/useCustomers';

const customersSearchSchema = z.object({
  page: z.coerce.number().int().positive().optional().default(1),
  limit: z.coerce.number().int().positive().optional().default(12),
  q: z.string().optional(),
  isArchived: z
    .preprocess((value) => value === 'true' || value === true, z.boolean())
    .optional()
    .default(false),
  sortBy: z
    .enum(['companyName', 'customerNumber', 'createdAt', 'updatedAt'])
    .optional()
    .default('companyName'),
  sortOrder: z.enum(['asc', 'desc']).optional().default('asc'),
});

type CustomersSearch = z.infer<typeof customersSearchSchema>;

export const Route = createFileRoute('/customers')({
  component: CustomersPage,
  validateSearch: customersSearchSchema,
});

function CustomersPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const query = Route.useSearch();
  const listQuery: CustomerListQuery = {
    ...query,
    q: query.q || undefined,
    isArchived: query.isArchived,
  };
  const updateSearch = (patch: Partial<CustomersSearch>) => {
    navigate({
      search: (previous: CustomersSearch) => ({ ...previous, ...patch }),
      replace: true,
    });
  };
  const { data, isLoading, error } = useCustomerList(listQuery);
  const goToCreateCustomer = () => {
    navigate({ to: '/customers/new' });
  };

  return (
    <ProtectedRoute>
      <AppShell>
        {/* Header — Sprint 006 A1: shared PageHeader */}
        <PageHeader
          title="Kunden"
          subtitle={`${data?.total ?? 0} Kunden${query.isArchived ? ' (archiviert)' : ''}`}
          actions={
            <Link to="/customers/new">
              <Button>
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Neuer Kunde
              </Button>
            </Link>
          }
        />

        {/* Filters — Sprint 006 A3: Suche links, Pills rechts (Kanon) */}
        <GlassPanel className="mb-6">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
            <div className="flex-1">
              <SearchInput
                placeholder="Suche nach Firma, Name oder E-Mail…"
                value={query.q ?? ''}
                onChange={(q) => updateSearch({ q: q || undefined, page: 1 })}
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <FilterPill
                active={!query.isArchived}
                onClick={() => updateSearch({ isArchived: false, page: 1 })}
              >
                Aktiv
              </FilterPill>
              <FilterPill
                active={!!query.isArchived}
                onClick={() => updateSearch({ isArchived: true, page: 1 })}
              >
                Archiviert
              </FilterPill>
            </div>
          </div>
        </GlassPanel>

        {/* Content */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : error ? (
          <GlassPanel>
            <div className="text-center py-8">
              <p className="text-red-400">Fehler beim Laden der Kunden</p>
            </div>
          </GlassPanel>
        ) : data?.customers.length === 0 ? (
          <EmptyState
            title={query.q ? 'Keine Treffer' : 'Keine Kunden'}
            description={
              query.q ? 'Versuchen Sie eine andere Suche' : 'Erstellen Sie Ihren ersten Kunden'
            }
            action={
              !query.q
                ? {
                    label: 'Kunde erstellen',
                    onClick: goToCreateCustomer,
                  }
                : undefined
            }
          />
        ) : (
          <>
            <GlassPanel
              variant="subtle"
              padding="sm"
              className="overflow-hidden mb-6"
              data-testid="customers-list-surface"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {data?.customers.map((customer) => (
                  <CustomerCard key={customer.id} customer={customer} />
                ))}
              </div>
            </GlassPanel>

            {data && data.totalPages > 1 && (
              <div className="flex justify-center">
                <Pagination
                  page={query.page}
                  totalPages={data.totalPages}
                  onPageChange={(page) => updateSearch({ page })}
                />
              </div>
            )}
          </>
        )}
      </AppShell>
    </ProtectedRoute>
  );
}
